import React, { useEffect, useState } from 'react';

import api from '../../../services/ApiManagement/Index';
import { useDebounce } from '../../../hooks/debounce';
import { useOnlineStatus } from '../../../hooks/onlineStatus';

import { IconCircleX, IconSearch, IconInfoCircle } from '@tabler/icons';

import Item from '../Item';
import Input from '../../Form/Input';
import { Container, IconWrapper, NotFoundItem, Row } from './styles';
import { Skeleton } from '../../Generic';

interface IRelativeInput {
  property_id: string;
  path: string;
  animalType?: 'macho' | 'femea';
  formRef?: any;
  defaultId?: string;
  onClear?: () => void;
}

const RelativeInput: React.FC<IRelativeInput> = ({
  property_id,
  path,
  animalType,
  defaultId,
  onClear,
}) => {
  const [query, setQuery] = useState<any>('');

  const [result, setResult] = useState<any>({});
  const [isSearching, setIsSearching] = useState(false);
  const [isCompatible, setIsCompatible] = useState(true);

  const isOnline = useOnlineStatus();

  const debouncedSearchTerm = useDebounce(query, 500);

  useEffect(() => {
    if (defaultId) {
      getDefault();
    }
  }, []);

  useEffect(() => {
    search();
  }, [debouncedSearchTerm]);

  const search = async () => {
    if (debouncedSearchTerm) {
      setIsSearching(true);
      setIsCompatible(true);

      try {
        const response = await api.get(
          `/pesquisas/animais/`,
          isOnline,
          'animais',
          {
            headers: {
              propriedade_id: property_id,
            },
            params: {
              pagina: 1,
              limite: 1,
              key: debouncedSearchTerm,
              estado: 'ativo',
              sexo: '',
              ano: '',
              categoria: '',
              raça: '',
              lote: '',
              safra: '',
              cursor_id: '',
            },
          },
        );

        const theAnimal = response.data.animais[0];

        setIsSearching(false);
        setResult(theAnimal);

        const currentRelative = path.replace('_id', '');

        if (theAnimal) {
          if (theAnimal.sexo === 'femea' && animalType === 'macho') {
            setIsCompatible(false);
            setResult(undefined);
          }

          if (theAnimal.sexo === 'macho' && currentRelative === 'mae') {
            setIsCompatible(false);
            setResult(undefined);
          }

          if (theAnimal.sexo === 'femea' && currentRelative === 'pai') {
            setIsCompatible(false);
            setResult(undefined);
          }
        }
      } catch (err) {
        console.log(err);
      }
    } else {
      setResult(undefined);
    }
  };

  const getDefault = async () => {
    setIsSearching(true);
    setIsCompatible(true);

    try {
      const response = await api.get(
        `/animais/${defaultId}`,
        isOnline,
        'animal',
        {
          headers: {
            propriedade_id: property_id,
          },
        },
      );

      setIsSearching(false);
      setResult(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Container>
      <Input
        label="ID"
        name={path}
        value={result?.id ? result.id : ''}
        customStyle={{ display: 'none' }}
      />

      <Input
        label="Pesquisar animal"
        name="pesquisa"
        placeholder="Digite o número ou o nome"
        value={query}
        onChange={(e: any) => setQuery(e.target.value)}
      />

      <IconWrapper>
        <IconSearch size={20} />
      </IconWrapper>

      {isSearching ? (
        <Skeleton style={{ height: 60, borderRadius: 8 }} />
      ) : result ? (
        <Row>
          <Item
            gender={result.sexo}
            name={result.nome}
            number={result.numero_de_identificacao}
            birth={result.data_de_nascimento}
            state={result.estado}
            belongs={result.pertencente}
            imageUrl={result.imagens?.[0]?.image_url}
          />
          <button
            type="button"
            onClick={() => {
              setResult(undefined);
              setQuery('');
            }}
          >
            <IconCircleX size={25} color="#999" />
          </button>
        </Row>
      ) : isCompatible ? (
        <NotFoundItem type="button" onClick={onClear}>
          <p>Não encontrado</p>
          <span>Cadastre o animal</span>
        </NotFoundItem>
      ) : (
        <NotFoundItem type="button" style={{ justifyContent: 'flex-start' }}>
          <IconInfoCircle size={20} color="#777" style={{ marginRight: 10 }} />
          <p>Este animal não é compatível</p>
        </NotFoundItem>
      )}
    </Container>
  );
};

export default RelativeInput;
